import { TYPE_APPOINTMENT_ENQUIRY } from 'constants/offeringTypes'
import {
  STATUS_CANCELLED,
  STATUS_PENDING_CONFIRMATION,
  STATUS_RESERVATION_EXPIRED,
  STATUS_RESERVED,
  STATUS_SETTLED,
} from 'constants/orderStatuses'
import { useCurrentOrder, useOrder, useReduxOrder } from 'contexts/Order'
import { chain, filter } from 'lodash'
import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectPackageOrderItemsIds } from 'selectors/OrdersSelectors'

export const useOrderId = () => {
  const { id } = useOrder()

  return id
}

export const useOrderRevenueDate = () => {
  const { booking_items_end_date: revenueDate } = useOrder()

  return revenueDate
}

export const useReduxOrderId = () => {
  const { id } = useReduxOrder()

  return id
}

export const useAllOrderItems = () => {
  const { order = {} } = useCurrentOrder()
  const {
    booking_items: bookingItems = [],
    package_items: packageItems = [],
    purchase_items: purchaseItems = [],
  } = order

  return [...bookingItems, ...packageItems, ...purchaseItems].sort(function (a, b) {
    return a.created_at.getTime() - b.created_at.getTime()
  })
}

export const useAllActiveOrderItems = () => {
  return useAllOrderItems().filter(({ status }) => status !== STATUS_CANCELLED)
}

/**
 * @deprecated
 */
export const usePackageOrderItemIds = () => {
  return useSelector(selectPackageOrderItemsIds(useReduxOrderId()))
}

export const useOrderFromReactQueryHasReservedItems = () => {
  const reservedBookingItems = useReservedBookingItems()
  const reservedPackageItems = useReservedPackageItems()
  const reservedPurchaseItems = useReservedPurchaseItems()

  return (
    reservedBookingItems.length > 0 ||
    reservedPackageItems.length > 0 ||
    reservedPurchaseItems.length > 0
  )
}

export const useOrderHasOnlyPurchaseItems = () => {
  const {
    order: {
      purchase_items: purchaseItems = [],
      booking_items: bookingItems = [],
      package_items: packageItems = [],
    } = {},
  } = useCurrentOrder()

  return purchaseItems.length > 0 && bookingItems.length === 0 && packageItems.length === 0
}

export const useReservedPurchaseItems = () => {
  const { order: { purchase_items: purchaseItems = [] } = {} } = useCurrentOrder()

  return chain(purchaseItems)
    .filter(({ status }) => [STATUS_RESERVED, STATUS_RESERVATION_EXPIRED].includes(status))
    .value()
}

export const useReservedBookingItems = () => {
  const { order: { booking_items: bookingItems = [] } = {} } = useCurrentOrder()

  return chain(bookingItems)
    .filter(({ status }) =>
      [STATUS_RESERVED, STATUS_RESERVATION_EXPIRED, STATUS_PENDING_CONFIRMATION].includes(status)
    )
    .value()
}

export const useReservedPackageItems = () => {
  const { order: { package_items: packageItems = [] } = {} } = useCurrentOrder()

  return chain(packageItems)
    .map('package_items')
    .flatten()
    .filter(({ booking_summary: { status = '' } = {} }) =>
      [STATUS_RESERVED, STATUS_RESERVATION_EXPIRED].includes(status)
    )
    .value()
}

export const useOrderStatus = () => {
  const { status } = useOrder()

  return status
}

export const useOrderStage = () => {
  const { stage } = useOrder()

  return stage
}

/**
 * @deprecated
 */
export const useReduxOrderCustomerDetails = () => {
  const {
    first_name: firstName = '',
    last_name: lastName = '',
    email = '',
    customer_id: customerId,
  } = useReduxOrder()

  const fullName = useMemo(
    () => (firstName || lastName ? filter([firstName, lastName]).join(' ') : undefined),
    [firstName, lastName]
  )

  return useMemo(
    () => ({
      id: customerId,
      first_name: firstName,
      last_name: lastName,
      full_name: fullName,
      email: email,
    }),
    [customerId, email, firstName, fullName, lastName]
  )
}

export const useAllActivePackageOrderItemDates = () => {
  const { order: { package_items: packageItems = [] } = {} } = useCurrentOrder()

  return chain(packageItems)
    .filter(({ status, date }) => status !== STATUS_CANCELLED && DateTime.fromJSDate(date).isValid)
    .map('date')
    .filter()
    .uniqBy((date) => date.getTime())
    .value()
}

export const useAllActiveBookingOrderItemDates = () => {
  const { order: { booking_items: bookingItems = [] } = {} } = useCurrentOrder()

  return chain(bookingItems)
    .filter(({ status, date }) => status !== STATUS_CANCELLED && DateTime.fromJSDate(date).isValid)
    .map('date')
    .filter()
    .uniqBy((date) => date.getTime())
    .value()
}

export const useAllActiveEnquiryDates = () => {
  const { order: { purchase_items: purchaseItems = [] } = {} } = useCurrentOrder()

  return chain(purchaseItems)
    .filter(
      ({ status, item_type: itemType }) =>
        status !== STATUS_CANCELLED && itemType === TYPE_APPOINTMENT_ENQUIRY
    )
    .map(({ item_configuration: itemConfiguration }) =>
      DateTime.fromISO(itemConfiguration.time_from).startOf('day').toJSDate()
    )
    .filter()
    .uniqBy((date) => date.getTime())
    .value()
}

export const useAllActiveOrderItemDates = () => {
  const bookingDates = useAllActiveBookingOrderItemDates()
  const packageDates = useAllActivePackageOrderItemDates()
  const enquiryDates = useAllActiveEnquiryDates()

  const dates = chain(bookingDates)
    .concat(packageDates)
    .concat(enquiryDates)
    .sort()
    .uniqBy((date) => date.getTime())
    .value()

  return dates
}

export const useOrderIsSettled = () => {
  const { data: { data: { status } = {} } = {} } = useCurrentOrder()

  return status === STATUS_SETTLED
}

export const useCanEditItem = (item) => {
  const itemIsCancelled = item.itemStatus === STATUS_CANCELLED
  const isOrderSettled = useOrderIsSettled()
  const isOrderLocked = useOrderIsLocked()

  return !itemIsCancelled && !isOrderSettled && !isOrderLocked
}

export const useOrderIsLocked = () => {
  const { is_locked: locked } = useCurrentOrder()

  return locked === true
}
